
// ai-assistant.js
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import { render, float32ArrayToWavBlob, logState, logError, displayError, getServerUrl } from './aiAssistantUtils.js';

// import  {MicVAD}  from "@ricky0123/vad-web"
// import * as ort from 'onnxruntime-web';
import { marked } from 'marked'; // Adjusted import

const port = window.location.port ? `:${window.location.port}` : '';
const userUrl = `${window.location.protocol}//${window.location.hostname}${port}`;

function loadScript(src) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
}   

async function loadAllResources() {
    try {
        await Promise.all([
            loadScript('https://cdn.jsdelivr.net/npm/onnxruntime-web@1.13.1/dist/ort.min.js'),
            loadScript('https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.7/dist/bundle.min.js'),
        ]);
        if (typeof ort === 'undefined' || typeof vad === 'undefined') {
            throw new Error('Required libraries not loaded properly');
        }
        console.log('All resources loaded successfully');
        initCustomElement();
    } catch (error) {
        console.error('Error loading resources', error);
    }
}


loadAllResources();

function initCustomElement() {
  class AIAssistant extends HTMLElement {
    constructor() {
            super();           
            this.attachShadow({ mode: 'open' });
            // this.shadowRoot.innerHTML = render();
            this.float32ArrayToWavBlob = float32ArrayToWavBlob;
            this.logState = logState;
            this.logError = logError;
            this.displayError = displayError;
            this.isListening = false;
            this.isPlayingAudio = false;
            this.audioContext = null;
            this.vadInstance = null;
            this.conversationTranscript = [];
            this.webSocket = null;
            this.userTimeString = '';

            this.gainNode = null;
            this.isRecording = false;
            this.isTextOnly = false;
            this.reconnectInterval = 5000;
            this.userLanguage = this.getUserLanguage();
            this.vadInstance = null;

        }
    
        connectedCallback() {
            this.accessKey = this.getAttribute('access-key');
            this.autoStart = this.hasAttribute('auto-start');
            this.selectedModel = this.getAttribute('selected-model');
            this.gltfModel = this.getAttribute('gltf-model') || 'trex.glb' ;
            this.serverUrl = getServerUrl();

            this.loadLanguageFile().then(() => {
                this.initializeUI();
                this.initThreeJS();
                this.startAssistant();
               

            this.shadowRoot.getElementById('disable-assistant').addEventListener('click', (event) => {
                event.preventDefault();
                this.toggleAssistant(event);
            });
    
            this.shadowRoot.getElementById('send-transcript').addEventListener('click', (event) => {
                event.preventDefault();
                this.sendTranscript(event);
            });
    
            this.shadowRoot.getElementById('show-transcript').addEventListener('click', (event) => {
                event.preventDefault();
                this.toggleTranscriptModal();
            });
    
            this.shadowRoot.querySelector('#close-modal').addEventListener('click', () => {
                this.hideTranscriptModal();
            });
            this.shadowRoot.querySelector('#expand-modal').addEventListener('click', () => {
                this.toggleTranscriptModalSize();
            });
            this.shadowRoot.querySelector('#close-three').addEventListener('click', (event) => {
                this.toggleAssistant(event);
                
            });

            this.shadowRoot.getElementById('powered-by').addEventListener('click', () => {
                window.open('https://webassistant.live', '_blank');
            });

            if (this.autoStart) {
                setTimeout((e) => this.toggleAssistant(e), 2000);
            }

            this.initWebSocket();
            this.setupMessageInput();

            // hide the modal if it is outside
            this.shadowRoot.addEventListener('click', this.handleShadowClick.bind(this));
            document.addEventListener('click', this.handleDocumentClick.bind(this));

            this.shadowRoot.getElementById('message-input').placeholder = this.t('Ask your question about the site...') + userUrl ;

            // this.disableAudioOutput();
            this.disableAudioOutput();
        });

        }

        // You might also want to add an attributeChangedCallback to react to changes
        static get observedAttributes() {
            return ['access-key', 'selected-model'];
        }

        attributeChangedCallback(name, oldValue, newValue) {
            console.log('Attribute changed:', name, 'from', oldValue, 'to', newValue);
            if (oldValue !== newValue) {
                if (name === 'selected-model') {
                    this.selectedModel = newValue;
                    // Add your logic here for when the model changes
                } 
            }
        }

        disconnectedCallback() {
            this.shadowRoot.removeEventListener('click', this.handleShadowClick.bind(this));
            document.removeEventListener('click', this.handleDocumentClick.bind(this));
        }
        handleShadowClick(event) {
            event.stopPropagation(); // Stop the event from propagating to the document
        }

        handleDocumentClick(event) {
            const modal = this.shadowRoot.getElementById('transcript-modal');
            if (modal.style.display === 'flex' && !this.shadowRoot.contains(event.target)) {
                this.hideTranscriptModal();
            }
        }

        getUserLanguage() {
            const fullLocale = navigator.language || navigator.userLanguage || 'en-US';
            return fullLocale.split('-')[0]; // This will return just the language code (e.g., 'en', 'es')
        }

        parsePO(poContent) {
            const translations = {};
            const lines = poContent.split('\n');
            let currentMsgid = '';
          
            for (const line of lines) {
              if (line.startsWith('msgid "')) {
                currentMsgid = line.slice(7, -1);
              } else if (line.startsWith('msgstr "') && currentMsgid) {
                translations[currentMsgid] = line.slice(8, -1);
              }
            }
          
            return translations;
          }
        
        async loadLanguageFile() {
            const language = this.getUserLanguage();
            try {
                const response = await fetch(`${this.serverUrl}/locales/messages_${language}.po`);
                const poContent = await response.text();
                this.translations = this.parsePO(poContent);
            } catch (error) {
                console.error('Error loading language file:', error);
                // Optionally, you could fall back to a default language here
                if (language !== 'en') {
                    console.log('Falling back to English');
                    await this.loadLanguageFile('en');
                }
            }
        }
        
        t(key) {
            return this.translations[key] || key;
          }

        initializeUI() {
            this.shadowRoot.innerHTML = render(this.t.bind(this));
            // Update placeholders and texts with translations
            this.shadowRoot.getElementById('message-input').placeholder = this.t('Ask your question about the site...');
        }

        initWebSocket() {
            this.websocket = new WebSocket(`${this.serverUrl.replace('http', 'ws')}/ws/${this.accessKey}/`);
            this.websocket.onopen = () => {
                console.log('WebSocket connection established');
                this.websocket.send(JSON.stringify({ 
                    type: 'init', 
                    accessKey: this.accessKey, 
                    model: this.selectedModel,
                    language: this.userLanguage,
                }));
            };
            this.websocket.onmessage = this.handleWebSocketMessage.bind(this);
            this.websocket.onerror = (error) => console.error('WebSocket error:', error);
            this.websocket.onclose = () => {
                console.log('WebSocket connection closed');
                setTimeout(() => {
                    console.log('Attempting to reconnect...');
                    this.initWebSocket();
                }, this.reconnectInterval);
            };        
        }
    
        async handleWebSocketMessage(event) {
            const message = JSON.parse(event.data);
            
            if (message.type === 'response') {
                console.log('Received message:', message);
                this.handleAssistantResponse(message.response, message.user_text, message.audioContent, message.context, message.total_cost);
            }
        }
        
        async handleAssistantResponse(responseText, user_text, audioContent, context, total_cost) {
            const now = new Date();
            const timeString = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
            if (!this.isTextOnly) this.updateTranscript('User', user_text, this.userTimeString);
            this.updateTranscript('Assistant', responseText, timeString, `${context}\n\nBu api çağrısı için toplam maliyet: ${total_cost}`);
            this.hideTypingAnimation();
    
            if (audioContent) {
                try {
                    const audioArrayBuffer = this.base64ToArrayBuffer(audioContent);
                    await this.speakResponse(audioArrayBuffer);
                } catch (error) {
                    console.error('Error playing audio:', error);
                    this.displayError('Failed to play audio response.');
                }
            }
        }

        initThreeJS() {
            const container = this.shadowRoot.getElementById('three-container');
            
            // Scene
            this.scene = new THREE.Scene();
            
            // Camera
            const width = container.clientWidth;
            const height = container.clientHeight;
            this.camera = new THREE.PerspectiveCamera(70, width / height, 0.01, 10);
            this.camera.position.z = 1;
            
            // Renderer
            this.renderer = new THREE.WebGLRenderer({ alpha: true });
            this.renderer.setSize(width, height);
            container.appendChild(this.renderer.domElement);
            
            // Add Lights
            this.addLights();
            
            // Load 3D Model
            const loader = new GLTFLoader();
            const gltfPath = `${this.serverUrl}/public/glb_models/${this.gltfModel}`;
            
            loader.load(
                gltfPath,
                (gltf) => {
                    this.model = gltf.scene;
                    this.scene.add(this.model);
            
                    // Position and rotate the model
                    this.positionAndRotateModel(this.model);
        
                    // Fit model to container (optional if required by your use case)
                    // this.fitModelToContainer();
                    this.resizeModel(this.model, 0.8);
            
                    // Set up animations
                    this.mixer = new THREE.AnimationMixer(this.model);
                    gltf.animations.forEach((clip) => {
                        this.mixer.clipAction(clip).play();
                    });
        
                    // Start the greeting sequence
                    this.playGreetingSequence();
            
                    // Adjust camera position if needed
                    this.adjustCameraPosition();
                },
                undefined, // Optional: Progress callback
                (error) => {
                    console.error('Error loading model:', error);
                }
            );
            
            // Initialize OrbitControls
            this.controls = new OrbitControls(this.camera, this.renderer.domElement);
            
            // Initialize THREE.Clock
            this.clock = new THREE.Clock();
            
            // Animation loop
            this.animate();
            
            // Add resize listener
            window.addEventListener('resize', this.handleResize.bind(this));
        
            if (this.autoStart) this.shadowRoot.querySelector('#three-container').style.display = 'none';
        }
        
        // Helper function to position and rotate the model
        positionAndRotateModel(model) {
            // Get bounding box of the model
            const bbox = new THREE.Box3().setFromObject(model);
            const size = bbox.getSize(new THREE.Vector3());
        
            // Position the model at the bottom-right corner of the container
            const offsetX = size.x / 2;
            const offsetY = size.y / 2;
            
            model.position.set(-offsetX, -offsetY, 0); // Bottom-right corner
        
            // Rotate the model 90 degrees on the X-axis (in radians)
            model.rotation.y = Math.PI / 2; // 90 degrees
        
            // Update world matrix after setting position/rotation
            model.updateMatrixWorld(true);
        }
        

        resizeModel(model, scale = 0.25) {
            if (!model) {
                console.error('Model is undefined or null in resizeModel');
                return;
            }
        
            // Get the bounding box of the model
            const bbox = new THREE.Box3();
        
            try {
                bbox.setFromObject(model);
            } catch (error) {
                console.error('Error computing bounding box:', error);
                return;
            }
        
            const size = bbox.getSize(new THREE.Vector3());
        
            // Calculate the scale factor
            const maxDim = Math.max(size.x, size.y, size.z);
            const scaleFactor = scale / maxDim;
        
            // Apply the scale
            model.scale.multiplyScalar(scaleFactor);
        
            // Center the model
            bbox.setFromObject(model);
            const center = bbox.getCenter(new THREE.Vector3());
            model.position.copy(center.multiplyScalar(-1));
        
            // Adjust the position if needed (e.g., to lift it off the "ground")
            model.position.y -= bbox.min.y;
        }
        
        
        handleResize() {
            const container = this.shadowRoot.getElementById('three-container');
            const width = container.clientWidth;
            const height = container.clientHeight;
        
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
            this.renderer.setSize(width, height);
        }
        
        // Add multiple lights to the scene
        addLights() {
            // Ambient Light - Softens shadows
            const ambientLight = new THREE.AmbientLight(0xffffff, 0.6); // Soft white light
            this.scene.add(ambientLight);

            // Directional Light - Simulates sunlight
            const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
            directionalLight.position.set(5, 10, 7.5);
            directionalLight.castShadow = true; // Enable shadow casting
            directionalLight.shadow.mapSize.width = 1024;
            directionalLight.shadow.mapSize.height = 1024;
            this.scene.add(directionalLight);

            // Point Light - Adds focus lighting
            const pointLight = new THREE.PointLight(0xffaa00, 1, 100);
            pointLight.position.set(0, 3, 2);
            pointLight.castShadow = true;
            this.scene.add(pointLight);

            // Hemisphere Light - Mixes sky and ground light
            const hemiLight = new THREE.HemisphereLight(0x4040ff, 0xff4040, 0.5);
            hemiLight.position.set(0, 5, 0);
            this.scene.add(hemiLight);
        }

        
        animate() {
            requestAnimationFrame(() => this.animate());
        
            const container = this.shadowRoot.getElementById('three-container');
            if (container.style.visibility === 'visible') {
                if (this.mixer) this.mixer.update(this.clock.getDelta());
                this.controls.update();
                this.renderer.render(this.scene, this.camera);
            }
        }

        adjustCameraPosition() {
            // Get the bounding box of the model
            const bbox = new THREE.Box3().setFromObject(this.model);
            const size = bbox.getSize(new THREE.Vector3());
            const center = bbox.getCenter(new THREE.Vector3());
        
            // Set camera position
            const maxDim = Math.max(size.x, size.y, size.z);
            const fov = this.camera.fov * (Math.PI / 180);
            let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
        
            // Adjust for desired zoom level
            cameraZ *= 1.5; // Increase this value to zoom out, decrease to zoom in
        
            this.camera.position.set(center.x, center.y, center.z + cameraZ);
            this.camera.lookAt(center);
            this.camera.updateProjectionMatrix();
        
            // Update controls target
            this.controls.target.copy(center);
            this.controls.update();
        }

        fitModelToContainer() {
            const container = this.shadowRoot.getElementById('three-container');
            const width = container.clientWidth;
            const height = container.clientHeight;
        
            // Get the bounding box of the model
            const bbox = new THREE.Box3().setFromObject(this.model);
            const size = bbox.getSize(new THREE.Vector3());
        
            // Calculate the scale to fit the container
            const scaleX = width / size.x;
            const scaleY = height / size.y;
            const scale = Math.min(scaleX, scaleY) * 0.9; // 0.9 to leave some margin
        
            this.model.scale.multiplyScalar(scale);
        
            // Center the model
            bbox.setFromObject(this.model);
            bbox.getCenter(this.model.position).multiplyScalar(-1);
        
            // Adjust the position if needed
            this.model.position.y -= bbox.min.y;
        
            // Adjust camera
            this.adjustCameraPosition();
        }
        
        playGreetingSequence() {
            // Play the knock animation
            // After the animation, greet the user
            this.speak(this.t('Hello, I am ready to assist you with the site.'));
        }

      
        async speak(text) {
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = this.userLanguage;
            window.speechSynthesis.speak(utterance);
        }


    async startAssistant() {
        
        try {
      
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    channelCount: 1,
                    sampleRate: 16000
                }
            });
            
            const mediaStreamSource = this.audioContext.createMediaStreamSource(stream);
            
            // Create a gain node to control volume
            this.gainNode = this.audioContext.createGain();
            this.gainNode.gain.value = 1.5; // Increase gain to make nearby voices louder
            
            // Create a high-pass filter to reduce low-frequency noise
            const highPassFilter = this.audioContext.createBiquadFilter();
            highPassFilter.type = 'highpass';
            highPassFilter.frequency.value = 85; // Filter out frequencies below 85 Hz
            
            // Create a low-pass filter to reduce high-frequency noise
            const lowPassFilter = this.audioContext.createBiquadFilter();
            lowPassFilter.type = 'lowpass';
            lowPassFilter.frequency.value = 3500; // Filter out frequencies above 3500 Hz
            
    
            mediaStreamSource.connect(this.gainNode);
            this.gainNode.connect(highPassFilter);
            highPassFilter.connect(lowPassFilter);
            lowPassFilter.connect(this.audioContext.destination);

            this.vadInstance = await vad.MicVAD.new({
                onSpeechStart: () => {
                    this.logState('Speech started, isListening: ' + this.isListening);
                    if (!this.isListening) return;        
                    this.isRecording = true;
                    this.playListeningAnimation();
                    this.disableAudioOutput();
                    
                },
                onSpeechEnd: async (audio) => {
                    this.logState('Speech ended, isListening: ' + this.isListening);
                    if (!this.isListening) return;
                    this.stopListeningAnimation();
                    
                    // Apply additional filtering to the audio before sending
                    const filteredAudio = this.applyAdditionalFiltering(audio);
                    
                    if (this.isSignificantSpeech(filteredAudio)) {
                        await this.sendRecording(filteredAudio);
                    } else {
                        this.logState('Speech discarded: not significant enough');
                        this.displayError('Speech discarded: not significant enough')
                    }
                },
                onVADMisfire: () => {
                    this.logState('VAD misfire, isListening: ' + this.isListening);
                    if (!this.isListening) return;
                    this.displayError(this.t('The audio is too short, please try again.'));
                },
                minSpeechFrames: 10, // Increase minimum speech frames to avoid short noises
                maxSilenceFrames: 10, // Reduce max silence frames to cut off quicker when speech ends
                frameSamples: 512,
                positiveSpeechThreshold: 0.8, // Increase threshold for more confident speech detection
                negativeSpeechThreshold: 0.25 // Lower threshold to be more sensitive to speech ending
            });
    
            // Connect the filtered stream to VAD
            await this.vadInstance.start(lowPassFilter);
            console.log('VAD instance started successfully with noise reduction');
        } catch (error) {
            console.error('Error setting up assistant:', error);
            console.error('ort object:', ort);
            console.error('vad object:', vad);
            this.displayError(this.t('Failed to start the assistant. Please check your microphone permissions and try again.'));
        }
    }

    disableAudioOutput() {
        if (this.gainNode) {
            this.gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
        }
    }

    enableAudioOutput() {
        if (this.gainNode) {
            this.gainNode.gain.setValueAtTime(1.5, this.audioContext.currentTime);
        }
    }

    applyAdditionalFiltering(audio) {
        // Implement additional filtering here if needed
        // For now, we'll just return the original audio
        return audio;
    }

    isSignificantSpeech(audio) {
        // Calculate the RMS (Root Mean Square) of the audio signal
        const rms = Math.sqrt(audio.reduce((sum, sample) => sum + sample * sample, 0) / audio.length);
        // Set a threshold for significant speech
        const threshold = 0.05; // Adjust this value as needed
        return rms > threshold;
    }

async sendRecording(audioBuffer = null, text = '', textOnly = false) {
        this.logState('Sending message');
        this.isTextOnly = textOnly;
        const now = new Date();
        this.userTimeString = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
        let messagePayload;
    
        if (textOnly || (!audioBuffer && text)) {
            // Text-only message
            messagePayload = {
                type: 'text',
                audio: audioBuffer,
                text: text
            };
        } else if (audioBuffer) {
            // Audio message (with optional transcription)
            const audioBlob = this.float32ArrayToWavBlob(audioBuffer);
            const base64Audio = await this.blobToBase64(audioBlob);
            messagePayload = {
                type: 'audio',
                audio: base64Audio,
                text: text ,
                model: this.selectedModel
            };
        } else {
            console.error('Invalid input: Neither audio nor text provided');
            this.displayError(this.t('Invalid input: Neither audio nor text provided'));
            return;
        }
  
        // Update transcript
        if (this.isTextOnly) this.updateTranscript('User', text , this.userTimeString);
         
        // Send the message
        if (this.websocket.readyState === WebSocket.OPEN) {
            this.showTypingAnimation();
            this.websocket.send(JSON.stringify(messagePayload));
        } else {
            console.error('WebSocket is not open. Attempting to reconnect...');
            this.initWebSocket();
            setTimeout(() => {
                if (this.websocket.readyState === WebSocket.OPEN) {
                    this.websocket.send(JSON.stringify(messagePayload));
                } else {
                    console.error('Reconnection attempt failed. Cannot send message');
                }
            }, this.reconnectInterval);
        }
    }

    sendMessage() {
        const messageInput = this.shadowRoot.getElementById('message-input');
        const message = messageInput.value.trim();
        if (message) {
            this.sendRecording(null, message, true);
            messageInput.value = '';
        }
    }

    showTypingAnimation() {
        this.shadowRoot.getElementById('typing-image').style.display = 'block';
        
    }

    hideTypingAnimation() {
        this.shadowRoot.getElementById('typing-image').style.display = 'none';
    }

    setupMessageInput() {
        const messageInput = this.shadowRoot.getElementById('message-input');
        const sendButton = this.shadowRoot.getElementById('send-message');

        messageInput.addEventListener('keypress', (event) => {
            if (event.key === 'Enter') {
                this.sendMessage();
            }
        });

        sendButton.addEventListener('click', () => {
            this.sendMessage();
        });
    }
    

    // Helper function to convert Blob to Base64
    blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }
    base64ToArrayBuffer(base64) {
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }


    async speakResponse(audioArrayBuffer) {
        this.disableAudioOutput();
        this.isPlayingAudio = true;
        if (!this.audioContext) {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }
        const audioBuffer = await this.audioContext.decodeAudioData(audioArrayBuffer);
        const source = this.audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(this.audioContext.destination);
        source.start();
        
        await new Promise(resolve => source.onended = resolve);
        this.isPlayingAudio = false;
        this.disableAudioOutput();
    }

    updateTranscript(sender, message, timestamp, context = "") {
        try {
            const modalTranscriptContainer = this.shadowRoot.getElementById('modal-transcript-container');
    
            const messageElement = document.createElement('div');
            messageElement.classList.add('transcript-message');
            messageElement.classList.add(sender.toLowerCase()); // Add CSS class based on sender (left or right)
    
            const contentElement = document.createElement('div');
            contentElement.classList.add('content');
    
            // Convert markdown to HTML using marked
            let parsedMessage = marked.parse(message);
    
            if (context !== "") {
                const contextToggleButton = `<button class="context-toggle" data-context="${encodeURIComponent(context)}">Source:[_]</button>`;
                parsedMessage += contextToggleButton;
            }
    
            contentElement.innerHTML = parsedMessage;
    
            const timestampElement = document.createElement('div');
            timestampElement.classList.add('timestamp');
            timestampElement.textContent = timestamp;
    
            messageElement.appendChild(contentElement);
            messageElement.appendChild(timestampElement);
    
            // Add event listener for context toggle
            if (context !== "") {
                const toggleButton = contentElement.querySelector('.context-toggle');
                toggleButton.addEventListener('click', this.handleContextToggle.bind(this));
            }
    
            modalTranscriptContainer.appendChild(messageElement);
    
            // Scroll to the bottom
            modalTranscriptContainer.scrollTop = modalTranscriptContainer.scrollHeight;
    
            // Double-check scroll after a short delay
            setTimeout(() => {
                modalTranscriptContainer.scrollTop = modalTranscriptContainer.scrollHeight;
            }, 100);
    
        } catch (error) {
            console.error('Error updating transcript:', error);
        }
    }
    
    handleContextToggle(event) {
        const button = event.target;
        const context = decodeURIComponent(button.getAttribute('data-context'));
        const contentElement = button.closest('.content');
    
        // Check if context contains a URL
        const urlPattern = /(https?:\/\/[^\s\{\}'"]+)/g;
        const urls = context.match(urlPattern);
    
        if (urls && urls.length > 0) {
            // Open the first URL found in a new tab
            window.open(urls[0], '_blank');
        } else {
            // If it's not a URL, toggle the source display as before
            if (button.textContent === 'Source:[_]') {
                const contextElement = document.createElement('div');
                contextElement.classList.add('context');
                contextElement.textContent = `...${context}...`;
                contentElement.insertBefore(contextElement, button.nextSibling);
                button.textContent = 'Hide Source:[^]';
            } else {
                const contextElement = contentElement.querySelector('.context');
                if (contextElement) {
                    contextElement.remove();
                }
                button.textContent = 'Source:[_]';
            }
        }
    }
    
    toggleTranscriptModal() {
        const modal = this.shadowRoot.getElementById('transcript-modal');
        if (modal.style.display === 'none' || modal.style.display === '') {
            this.showTranscriptModal();
        } else {
            this.hideTranscriptModal();
        }
    }
  
    toggleTranscriptModalSize() {
        const modal = this.shadowRoot.getElementById('transcript-modal');
        if (modal.style.width === '50%' || modal.style.width === '') {
            modal.style.width = '90%';
        } else {
            modal.style.width = '50%';
        }

    }
    showTranscriptModal() {
        const modal = this.shadowRoot.getElementById('transcript-modal');
        modal.style.display = 'flex';
    }

    hideTranscriptModal() {
        const modal = this.shadowRoot.getElementById('transcript-modal');
        modal.style.display = 'none';
    }

    async sendTranscript() {
        const email = this.shadowRoot.getElementById('user-email').value;
        const transcript = this.conversationTranscript.map(entry => `${entry.sender}: ${entry.message}`).join('\n');

        const payload = {
            email,
            transcript,
            access_key: this.accessKey
        };

        try {
            const response = await fetch(`${this.serverUrl}/send-transcript/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Transcript sent:', data);
           
        } catch (error) {
            console.error('Error sending transcript:', error);
        }
    }

    checkForEndOfConversation(response) {
        if (response.includes('Goodbye') || response.includes('bye')) {
            this.showEmailForm();
        }
    }

    showEmailForm() {
        const emailForm = this.shadowRoot.getElementById('email-form');
        emailForm.style.display = 'flex';
    }
      
    playListeningAnimation() {
        const imgElement = this.shadowRoot.getElementById('disable-assistant-img');
        imgElement.src = `${this.serverUrl}/public/img/recordbutton.gif`;
    }
    stopListeningAnimation() {
        const imgElement = this.shadowRoot.getElementById('disable-assistant-img');
        imgElement.src = `${this.serverUrl}/public/img/off.png`;
    }

    async toggleAssistant(event) {
        this.isListening = !this.isListening;
        if (!this.vadInstance) await this.startAssistant();
        
        try {
            this.logState(`toggleAssistant called ${JSON.stringify({ event, isListening: this.isListening })}`);
            if (event && event.preventDefault) {
                event.preventDefault();
                event.stopPropagation();
            }
            
            const threeContainer = this.shadowRoot.getElementById('three-container');

            if (this.isListening) {
                threeContainer.style.visibility = 'visible';
                await this.vadInstance.start(this.audioContext.createMediaStreamSource(this.vadInstance.stream));
                this.enableAudioOutput();
                
            } else {
                threeContainer.style.visibility = 'hidden';
                this.logState('Pausing VAD instance');
                this.vadInstance.pause();
                this.disableAudioOutput();
            }
            
            const buttonImg = this.shadowRoot.getElementById('disable-assistant-img');
            buttonImg.src = this.isListening ?  `${this.serverUrl}/public/img/off.png` :`${this.serverUrl}/public/img/on.png`;
            this.logState('Assistant status: ' + this.isListening);
            

        } catch (error) {
            console.error('Error in toggleAssistant:', error);
        }
        
    }
    
}

customElements.define('ai-assistant', AIAssistant);
}
